import React, { useEffect, useState } from "react";
import { IconButton } from "@shared/components/IconButton";
import { usePreviousReadingImage } from "../../hooks";
import { useRemoteUtilityId } from "@shared/selectors/globalSelectors";
import { useValidationContext } from "../../context";
import { Dialog, DialogContent, DialogTitle } from "@shared/ui/dialog";
import { Maximize2 } from "lucide-react";

import { cn } from "@shared/lib/utils";
import { ReadingImageItem } from "../../types";


type Props = {
  imageUrl?: string;
  previousReadingId?: any;
  showPrevious?: boolean;
  currentImages?: ReadingImageItem[];
};

function ImageCard({ imageUrl, previousReadingId, showPrevious = false, currentImages }: Props) {
  const remoteUtilityId = useRemoteUtilityId();
  const { readingId } = useValidationContext();

  // Fetch previous image data
  const { data } = usePreviousReadingImage({
    remoteUtilityId,
    readingId: previousReadingId,
    showPrevious: showPrevious,
  });

  const [fullscreenImage, setFullscreenImage] = useState<string | null>(null);
  const [selectedIndex, setSelectedIndex] = useState(0);

  // Normalize previous images data structure based on strict API response
  const fetchedPreviousImages: ReadingImageItem[] = React.useMemo(() => {
    if (!data) return [];
    const responseData = data as any;
    return responseData.currentReadingImages || [];
  }, [data]);

  const activeImages = showPrevious ? fetchedPreviousImages : currentImages;
  const hasArrayImages = activeImages && activeImages.length > 0;

  // Reset selected index when switching views
  useEffect(() => {
    setSelectedIndex(0);
  }, [showPrevious, activeImages]);

  const displayUrl = hasArrayImages 
    ? activeImages[selectedIndex]?.readingImage 
    : (showPrevious ? null : imageUrl);

  return (
    <div className="bg-white rounded-md shadow-sm border w-full h-full flex flex-col">
      <div className="bg-muted mb-2 flex items-center justify-between p-2">
        <h4 className="text-sm font-semibold text-gray-700">
          {showPrevious ? "Previous Image" : "Current Image"}
        </h4>
        {displayUrl && (
          <button
            type="button"
            onClick={() => setFullscreenImage(displayUrl)}
            className="text-gray-500 hover:text-gray-900 transition-colors p-1 rounded hover:bg-gray-200"
            title="Zoom Image"
          >
            <Maximize2 className="h-4 w-4" />
          </button>
        )}
      </div>

      <div className="flex-1 min-h-0 flex flex-col px-2 pb-4 gap-4">
        <div className="flex-1 flex items-center justify-center min-h-0 w-full">
          {displayUrl ? (
            <img
              src={displayUrl}
              alt={showPrevious ? "Previous reading image" : "Current reading image"}
              className="object-contain w-full h-full cursor-pointer max-h-[300px]"
              onClick={() => setFullscreenImage(displayUrl)}
            />
          ) : (
            <p className="text-sm text-muted-foreground text-center">
              {showPrevious ? "Previous reading not available" : "Current reading not available"}
            </p>
          )}
        </div>
        
        {hasArrayImages && activeImages.length > 1 && (
          <div className="flex flex-wrap gap-2 justify-center mt-auto pt-2">
            {activeImages.map((img, idx) => (
              <button
                key={`${img.readingId}-${idx}`}
                onClick={() => setSelectedIndex(idx)}
                className={cn(
                  "px-3 py-1 text-xs rounded-md border transition-colors",
                  selectedIndex === idx
                    ? "bg-primary text-primary-foreground border-primary"
                    : "bg-background hover:bg-muted text-muted-foreground border-border"
                )}
              >
                {img.name}
              </button>
            ))}
          </div>
        )}
      </div>

      <Dialog open={!!fullscreenImage} onOpenChange={() => setFullscreenImage(null)}>
        <DialogContent className="max-w-5xl bg-transparent border-none shadow-none flex justify-center items-center [&>button]:text-white">
          <DialogTitle className="sr-only">Meter Image Fullscreen</DialogTitle>
          {fullscreenImage && (
            <img src={fullscreenImage} alt="Fullscreen image" className="max-w-full max-h-[85vh] object-contain" />
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

export default ImageCard;
